import React, { useMemo, useEffect, useRef } from 'react';
import { TitleFull as Title } from '@warehouse/title/core';
import { IndexedTitleV2 } from '@warehouse/graphql';
import { WorkType } from '@warehouse/title-shared/core';
import { ServerDataGrid } from '@warehouse/shared/ui';
import { titleEditorStoreSelector, useTitleEditorStore } from '@warehouse/title/domain';
import { useChildrenColumnsPerWorkType } from '../useChildrenColumnsPerWorkType';
import { mapEntryClassAndEntryNumberToData } from '../helpers/map-entry-class-and-entry-number-to-data';
import { childTableStoreSelector } from '../domain/children-table.store.selectors';
import { useChildrenListStore } from '../domain/children.store';
import { ChildTableCell } from './child-table-cell/ChildTableCell';
import { useEditEntryNumber } from '../inline-edit/entry-number/use-edit-entry-number';
import useWorkTypes from '../../../../src/utils/hooks/titles/useWorkTypes';
import { StyledDataGrid } from './ChildTable.styles';
import { childrenGridConfig } from './children.grid-config';
import { getTitleWorkTypeUuid } from '../children-table-type';

type ChildTableProps = {
	title: Title;
	onClickRow?: (row: IndexedTitleV2) => void;
	childWorkType: WorkType;
	disableVirtualization?: boolean;
};

export function ChildTable({ title, childWorkType, onClickRow, disableVirtualization }: ChildTableProps) {
	const editMode = useTitleEditorStore(titleEditorStoreSelector.editMode);

	const workTypeUuid = useMemo(() => getTitleWorkTypeUuid(title), [title]);

	const workTypes = useWorkTypes();
	const compilationUuid = workTypes.compilationUuid();
	const manifestationUuid = workTypes.manifestationUuid();

	const items = useChildrenListStore(childTableStoreSelector.items);
	const columnVisibility = useChildrenListStore((state) => state.visibility);
	const setSearchFields = useChildrenListStore((state) => state.actions.setSearchFields);

	// Create a shared editEntryNumber instance that will be passed to all cells
	const editEntryNumber = useEditEntryNumber();

	const [columns, searchFields] = useChildrenColumnsPerWorkType(
		workTypeUuid,
		childWorkType,
		editEntryNumber,
		columnVisibility,
	);

	/*
	 * Compares array contents, not references
	 * Tracks previous searchFields to avoid infinite loops
	 * Update search fields in store when they change (only if actually different)
	 */
	const prevSearchFieldsRef = useRef<string[] | undefined>(undefined);

	useEffect(() => {
		if (!searchFields) return;

		const prevSearchFields = prevSearchFieldsRef.current;

		const hasChanged =
			!prevSearchFields ||
			prevSearchFields.length !== searchFields.length ||
			!searchFields.every((field, index) => field === prevSearchFields[index]);

		if (hasChanged) {
			prevSearchFieldsRef.current = searchFields;
			setSearchFields(searchFields);
		}
	}, [searchFields, setSearchFields]);

	const rows = useMemo(
		() =>
			workTypeUuid === compilationUuid || workTypeUuid === manifestationUuid
				? mapEntryClassAndEntryNumberToData(items, title)
				: items,
		[title, workTypeUuid, items, compilationUuid, manifestationUuid],
	);
	const enhancedColumns = columns.map((column) => {
		let defaultCellClassName = '';
		if (column.field === 'titleSearchDisplayNameFull' || column.field === 'entryNumber') {
			defaultCellClassName = 'no-padding-cell';
		} else {
			defaultCellClassName = 'padding-cell';
		}

		return {
			...column,
			cellClassName: (params: any) => {
				const originalClassName =
					typeof column.cellClassName === 'function' ? column.cellClassName(params) : column.cellClassName || '';

				return `${column.field === 'entryNumber' && editMode ? 'editable-cell' : ''} ${defaultCellClassName} ${originalClassName}`.trim();
			},
		};
	});

	return (
		<div className="child-table-wrapper">
			<ServerDataGrid
				gridComponent={StyledDataGrid}
				$variant="full"
				columns={enhancedColumns}
				rows={rows}
				useStore={useChildrenListStore}
				selector={childTableStoreSelector}
				config={childrenGridConfig}
				getRowId={(row) => row.uuid}
				rowSelection={false}
				autoHeight
				rowHeight={50}
				slots={{
					cell: ChildTableCell,
				}}
				onCellClick={(params) => {
					const selection = document.getSelection();
					if (selection && selection.type === 'Range') return;

					// Don't trigger row click for entry number cells in edit mode
					if (params.field === 'entryNumber' && editMode) return;

					if (onClickRow && !params.isEditable) onClickRow(params.row);
				}}
				disableVirtualization={disableVirtualization}
			/>
		</div>
	);
}
